// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './TowAnimation.css';

const TowAnimation = ({ isActive, destinationName, onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isActive) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => {
              onComplete();
            }, 500);
            return 100;
          }
          return prev + 1;
        });
      }, 50); // 5 second animation

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]); // onComplete intentionally excluded - it's a callback that shouldn't trigger re-runs

  if (!isActive) return null;

  return createPortal(
    <div className="tow-animation-overlay">
      <div className="tow-animation-container">
        <div className="tow-title">🚨 EMERGENCY TOW SERVICE 🚨</div>
        <div className="tow-subtitle">Towing to {destinationName}</div>
        
        <div className="tow-scene">
          {/* Starfield background */}
          <div className="tow-stars">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="tow-star"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `twinkle 2s ease-in-out infinite ${Math.random() * 2}s`
                }}
              />
            ))}
          </div>

          {/* Your damaged ship (being towed) - BEHIND the tow ship */}
          <div className="towed-ship" style={{ left: `${20 + progress * 0.6}%` }}>
            <svg width="50" height="50" viewBox="0 0 40 40">
              {/* Saucer section - damaged */}
              <ellipse cx="20" cy="15" rx="12" ry="6" fill="rgba(255, 68, 68, 0.2)" stroke="#ff4444" strokeWidth="1.5" strokeDasharray="2,2"/>
              
              {/* Neck */}
              <rect x="18" y="18" width="4" height="6" fill="rgba(255, 68, 68, 0.2)" stroke="#ff4444" strokeWidth="1"/>
              
              {/* Engineering hull */}
              <ellipse cx="20" cy="28" rx="6" ry="8" fill="rgba(255, 68, 68, 0.2)" stroke="#ff4444" strokeWidth="1.5"/>
              
              {/* Left nacelle - damaged */}
              <rect x="8" y="25" width="4" height="10" rx="2" fill="rgba(255, 68, 68, 0.2)" stroke="#ff4444" strokeWidth="1" strokeDasharray="1,1"/>
              
              {/* Right nacelle - damaged */}
              <rect x="28" y="25" width="4" height="10" rx="2" fill="rgba(255, 68, 68, 0.2)" stroke="#ff4444" strokeWidth="1" strokeDasharray="1,1"/>
              
              {/* Damage sparks */}
              <circle cx="12" cy="20" r="1.5" fill="#ffaa00">
                <animate attributeName="opacity" values="0;1;0" dur="0.3s" repeatCount="indefinite"/>
              </circle>
              <circle cx="28" cy="22" r="1.5" fill="#ffaa00">
                <animate attributeName="opacity" values="0;1;0" dur="0.4s" begin="0.2s" repeatCount="indefinite"/>
              </circle>
              
              {/* Dead engines (no glow) */}
              <ellipse cx="10" cy="36" rx="2" ry="3" fill="#666666" opacity="0.3"/>
              <ellipse cx="30" cy="36" rx="2" ry="3" fill="#666666" opacity="0.3"/>
            </svg>
          </div>

          {/* Tow cable with energy field - connects towed ship to tow ship */}
          <div 
            className="tow-cable" 
            style={{ 
              left: `${25 + progress * 0.6}%`,
              width: `${15}%`
            }}
          >
            {/* Main cable line */}
            <div className="tow-cable-line" />
            
            {/* Energy pulses using CSS animations */}
            <div className="tow-energy-pulse pulse-1" />
            <div className="tow-energy-pulse pulse-2" />
            <div className="tow-energy-pulse pulse-3" />
          </div>

          {/* Rescue tug ship (pulling) - IN FRONT pulling the damaged ship */}
          <div className="tow-ship" style={{ left: `${40 + progress * 0.6}%` }}>
            <svg width="60" height="50" viewBox="0 0 50 40">
              {/* Tow ship main hull - robust design */}
              <rect x="10" y="15" width="30" height="12" rx="6" fill="rgba(0, 255, 255, 0.3)" stroke="#00ffff" strokeWidth="2"/>
              
              {/* Command section */}
              <rect x="15" y="10" width="20" height="8" rx="4" fill="rgba(0, 255, 255, 0.4)" stroke="#00ffff" strokeWidth="1.5"/>
              
              {/* Tow equipment/winch */}
              <rect x="5" y="18" width="8" height="6" rx="2" fill="rgba(255, 170, 0, 0.4)" stroke="#ffaa00" strokeWidth="1.5"/>
              
              {/* Engine pods */}
              <rect x="12" y="28" width="6" height="8" rx="3" fill="rgba(0, 255, 255, 0.3)" stroke="#00ffff" strokeWidth="1"/>
              <rect x="32" y="28" width="6" height="8" rx="3" fill="rgba(0, 255, 255, 0.3)" stroke="#00ffff" strokeWidth="1"/>
              
              {/* Active engines with glow */}
              <ellipse cx="15" cy="37" rx="3" ry="2" fill="#00ffff" opacity="0.8">
                <animate attributeName="opacity" values="0.8;1;0.8" dur="0.5s" repeatCount="indefinite"/>
              </ellipse>
              <ellipse cx="35" cy="37" rx="3" ry="2" fill="#00ffff" opacity="0.8">
                <animate attributeName="opacity" values="0.8;1;0.8" dur="0.5s" begin="0.25s" repeatCount="indefinite"/>
              </ellipse>
              
              {/* Tow beacon */}
              <circle cx="25" cy="14" r="2" fill="#ff4444">
                <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite"/>
              </circle>
              
              {/* Navigation lights */}
              <circle cx="42" cy="21" r="1" fill="#00ff88"/>
              <circle cx="8" cy="21" r="1" fill="#ff4444"/>
            </svg>
          </div>
        </div>

        <div className="tow-progress-container">
          <div className="tow-progress-bar">
            <div 
              className="tow-progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="tow-progress-text">{progress}% Complete</div>
        </div>

        <div className="tow-message">
          Emergency services responding... Stand by.
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TowAnimation;
